import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useMonth } from '@/contexts/MonthContext';
import { buildExtendedMonthList } from '@/lib/months';
import { useAuth } from '@/contexts/AuthContext';
import { useDisplayCurrency } from '@/hooks/useDisplayCurrency';
import { fmtMoney, getDisplayCurrency } from '@/lib/currency';
import WelcomeMessage from '@/components/WelcomeMessage';
import BadgeCelebration from '@/components/BadgeCelebration';
import UserBadge from '@/components/UserBadge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DollarSign, TrendingDown, Wallet, PiggyBank,
  Activity, ArrowUpRight, ArrowDownRight, ChevronLeft, ChevronRight, Calendar, Info, Award
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';

const COLORS = ['hsl(136,18%,35%)', 'hsl(43,74%,50%)', 'hsl(8,52%,51%)', 'hsl(197,37%,44%)', 'hsl(280,45%,55%)', 'hsl(30,70%,50%)', 'hsl(180,40%,40%)', 'hsl(350,60%,50%)'];
const fmt = (n) => fmtMoney(n);
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const getMonthLabel = (y, m) => `${MONTH_NAMES[parseInt(m, 10) - 1]} ${y}`;

// Category groupings - maps display name to actual categories
const CATEGORY_GROUPS = {
  "Dining Out": ["Coffee", "Restaurants", "Food Takeout"]
};
const navMonth = (y, m, d) => {
  let mi = parseInt(m, 10) + d, yi = parseInt(y, 10);
  while (mi > 12) { mi -= 12; yi++; } while (mi < 1) { mi += 12; yi--; }
  return { year: String(yi), month: String(mi).padStart(2, '0') };
};

function StatCard({ icon: Icon, label, value, subtext, trend, testId }) {
  const isPos = trend === 'positive';
  const TI = isPos ? ArrowUpRight : ArrowDownRight;
  return (
    <Card className="border border-border shadow-none hover:-translate-y-0.5 hover:shadow-md transition-all duration-200" data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">{label}</p>
            <p className="text-2xl font-semibold tracking-tight font-['Outfit'] tabular-nums">{value}</p>
            {subtext && <div className="flex items-center gap-1">{trend && <TI className={`h-3 w-3 ${isPos ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`} />}<span className={`text-xs ${isPos ? 'text-green-600 dark:text-green-400' : trend === 'negative' ? 'text-red-500 dark:text-red-400' : 'text-muted-foreground'}`}>{subtext}</span></div>}
          </div>
          <div className="h-9 w-9 rounded-md bg-primary/10 flex items-center justify-center shrink-0"><Icon className="h-4 w-4 text-primary" /></div>
        </div>
      </CardContent>
    </Card>
  );
}

function BudgetRuleCard({ data }) {
  if (!data || !data.baseline) return null;
  const buckets = [
    { key: 'needs', label: 'Needs', target: 50, color: 'hsl(136,18%,35%)', desc: 'Essentials: housing, groceries, utilities, insurance', higherIsBad: true },
    { key: 'wants', label: 'Wants', target: 30, color: 'hsl(43,74%,50%)', desc: 'Lifestyle: dining, entertainment, shopping, travel', higherIsBad: true },
    { key: 'savings', label: 'Savings', target: 20, color: 'hsl(197,37%,44%)', desc: 'Future: investments, savings, debt payoff', higherIsBad: false },
  ];
  return (
    <Card className="border border-border shadow-none" data-testid="budget-rule-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Activity className="h-4 w-4 text-primary" />
          50 / 30 / 20 Budget Rule
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {buckets.map((b) => {
            const d = data[b.key];
            const pct = d?.percentage || 0;
            const isGood = b.higherIsBad ? pct <= b.target + 5 : pct >= b.target - 5;
            return (
              <div key={b.key}>
                <div className="flex items-center justify-between mb-1">
                  <div>
                    <span className="text-sm font-medium">{b.label}</span>
                    <span className="text-xs text-muted-foreground ml-2">{b.desc}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-semibold tabular-nums ${isGood ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>{pct}%</span>
                    <span className="text-xs text-muted-foreground">/ {b.target}%</span>
                  </div>
                </div>
                <div className="h-2.5 bg-muted rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: isGood ? b.color : 'hsl(8,52%,51%)' }} />
                </div>
                <div className="flex justify-between text-xs text-muted-foreground mt-0.5">
                  <span>{fmt(d?.amount || 0)} spent</span>
                  <span>Target: {fmt(d?.target_amount || 0)}</span>
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-4 p-3 bg-muted/50 rounded-md border border-border">
          <div className="flex items-start gap-2">
            <Info className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
            <p className="text-xs text-muted-foreground italic leading-relaxed">&ldquo;{data.buffett_quote}&rdquo;<br /><span className="font-medium not-italic">— Warren Buffett</span></p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  useDisplayCurrency(); // re-render when user changes base currency
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [summary, setSummary] = useState(null);
  const [health, setHealth] = useState(null);
  const [budgetRule, setBudgetRule] = useState(null);
  const [availableMonths, setAvailableMonths] = useState([]);
  const { year: currentYear, month: currentMonth, setMonth } = useMonth();
  const [loading, setLoading] = useState(true);
  
  // Use sessionStorage to track if welcome message was shown this session
  const [welcomeDismissed, setWelcomeDismissed] = useState(() => {
    return sessionStorage.getItem('welcomeShownThisSession') === 'true';
  });
  
  const [showCelebration, setShowCelebration] = useState(false);
  const showWelcome = !!(user && user.login_count && user.login_count <= 3 && !welcomeDismissed);

  // Check if we should show celebration (first time after tipping/subscribing)
  useEffect(() => {
    if (user && user.celebration_shown === false && user.badge) {
      setShowCelebration(true);
    }
  }, [user]);
  
  // Handle welcome message close - mark as shown for this session
  const handleWelcomeClose = () => {
    setWelcomeDismissed(true);
    sessionStorage.setItem('welcomeShownThisSession', 'true');
  };

  const handleCloseCelebration = async () => {
    setShowCelebration(false);
    // Mark celebration as shown
    try {
      await api.post('/api/auth/mark-celebration-shown');
      await checkAuth(); // Refresh user state
    } catch (error) {
      console.error('Failed to mark celebration as shown:', error);
    }
  };

  useEffect(() => { api.get('/api/dashboard/available-months').then(({ data }) => setAvailableMonths(data || [])).catch(() => {}); }, []);

  const loadData = useCallback(async (year, month) => {
    setLoading(true);
    try {
      const params = {};
      if (year && month) { params.year = year; params.month = month; }
      const [sumRes, healthRes, brRes] = await Promise.all([
        api.get('/api/dashboard/summary', { params }),
        api.get('/api/dashboard/financial-health', { params }),
        api.get('/api/dashboard/budget-rule', { params }),
      ]);
      setSummary(sumRes.data);
      setHealth(healthRes.data);
      setBudgetRule(brRes.data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(currentYear, currentMonth); }, [loadData, currentYear, currentMonth]);

  const handlePrev = () => { if (currentYear && currentMonth) { const n = navMonth(currentYear, currentMonth, -1); setMonth(n.year, n.month); } };
  const handleNext = () => { if (currentYear && currentMonth) { const n = navMonth(currentYear, currentMonth, 1); setMonth(n.year, n.month); } };

  // Click handlers for interactive charts
  const handlePieClick = (entry) => {
    if (entry?.name && currentYear && currentMonth) {
      // Check if this is a grouped category
      const categories = CATEGORY_GROUPS[entry.name];
      if (categories && categories.length > 0) {
        // Navigate with multiple categories (group filter)
        const categoryParam = categories.map(c => encodeURIComponent(c)).join(',');
        navigate(`/transactions?year=${currentYear}&month=${currentMonth}&categories=${categoryParam}`);
      } else {
        // Single category
        navigate(`/transactions?year=${currentYear}&month=${currentMonth}&category=${encodeURIComponent(entry.name)}`);
      }
    }
  };
  const handleDailyClick = (data) => {
    if (data?.activePayload?.[0]?.payload?.date) {
      const d = data.activePayload[0].payload.date;
      navigate(`/transactions?year=${currentYear}&month=${currentMonth}&search=${d}`);
    }
  };
  const handleMerchantClick = (merchant) => {
    if (merchant) navigate(`/transactions?year=${currentYear}&month=${currentMonth}&search=${encodeURIComponent(merchant)}`);
  };

  const extendedMonths = useMemo(() => buildExtendedMonthList(availableMonths, 24, 12), [availableMonths]);

  if (loading && !summary) {
    return <div className="space-y-6 animate-pulse"><div className="h-8 w-48 bg-muted rounded" /><div className="grid grid-cols-1 md:grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <div key={i} className="h-28 bg-muted rounded-md" />)}</div></div>;
  }

  const s = summary || {};
  const h = health || {};
  const catData = Object.entries(s.category_breakdown || {}).sort((a, b) => b[1] - a[1]).map(([name, value]) => ({ name, value: Math.round(value) }));
  const dailyData = (s.daily_expenses || []).map((d) => ({ day: d.day, date: d.date, amount: d.amount }));
  const monthLabel = currentYear && currentMonth ? getMonthLabel(currentYear, currentMonth) : '';

  return (
    <>
      <BadgeCelebration
        badge={user?.badge}
        show={showCelebration}
        onClose={handleCloseCelebration}
      />
      
      <div className="space-y-6" data-testid="dashboard-page">
        <WelcomeMessage open={showWelcome} onClose={handleWelcomeClose} loginCount={user?.login_count} />
        

        
        {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight font-['Outfit']">Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">{s.transaction_count || 0} transactions</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={handlePrev} data-testid="prev-month-btn"><ChevronLeft className="h-4 w-4" /></Button>
          {extendedMonths.length > 0 ? (
            <Select value={currentYear && currentMonth ? `${currentYear}-${currentMonth}` : ''} onValueChange={(v) => { const [y, m] = v.split('-'); setMonth(y, m); }}>
              <SelectTrigger className="w-[170px] h-8 text-sm" data-testid="month-selector"><Calendar className="h-3.5 w-3.5 mr-1.5" /><SelectValue placeholder={monthLabel} /></SelectTrigger>
              <SelectContent className="max-h-[400px]">
                {extendedMonths.map((em) => (
                  <SelectItem key={em.month} value={em.month}>
                    <span className={em.hasData ? '' : 'text-muted-foreground'}>{em.label}</span>
                    {em.hasData && <span className="text-xs text-muted-foreground ml-1">({em.count})</span>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : <span className="text-sm font-medium px-3">{monthLabel}</span>}
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={handleNext} data-testid="next-month-btn"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      {/* Stats - Interactive */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="cursor-pointer" onClick={() => navigate('/income')} data-testid="stat-income">
          <StatCard icon={DollarSign} label={s.tax_enabled ? 'Gross / Net Income' : 'Monthly Income'} value={fmt(s.income)} subtext={s.tax_enabled ? `Net: ${fmt(s.net_income)} · Tax: ${fmt(s.tax_owed_month)}` : null} />
        </div>
        <div className="cursor-pointer" onClick={() => navigate(`/transactions?year=${currentYear}&month=${currentMonth}`)} data-testid="stat-expenses">
          <StatCard icon={TrendingDown} label="Net Expenses" value={s.income > 0 ? `${Math.round((s.net_expenses || s.expenses) / s.income * 100)}%` : '–'} trend="negative" subtext={`${fmt(s.net_expenses || s.expenses)} · ${s.refunds > 0 ? `${s.transaction_count} txns · ${fmt(s.refunds)} refunded` : `${s.transaction_count || 0} txns`}`} />
        </div>
        <div className="cursor-pointer" onClick={() => navigate('/analytics')} data-testid="stat-savings">
          <StatCard icon={PiggyBank} label="Effective Savings" value={fmt(s.savings)} subtext={<span className={(s.net_savings_ytd || 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}>{`${s.savings_rate || 0}% · YTD: ${fmt(s.net_savings_ytd || 0)}${s.sip_invested ? ` · SIP: ${fmt(s.sip_invested)}` : ''}`}</span>} trend={s.savings >= 0 ? 'positive' : 'negative'} />
        </div>
        <div className="cursor-pointer" onClick={() => navigate('/networth')} data-testid="stat-networth">
          <StatCard icon={Wallet} label={`Net Worth (${getDisplayCurrency()})`} value={fmt(s.net_worth)} subtext={<><span className="text-green-600 dark:text-green-400">A: {fmt(s.total_assets)}</span><span className="text-muted-foreground"> · </span><span className="text-red-500 dark:text-red-400">L: {fmt(s.total_liabilities)}</span></>} trend={s.net_worth >= 0 ? 'positive' : 'negative'} />
        </div>
      </div>

      {/* Secondary metrics row (v1.3.1): Debt Flow %, Investment Flow %, Tax Owed YTD (only when applicable) */}
      {(s.income > 0 || s.tax_enabled) && (
        <div className={`grid grid-cols-2 ${s.tax_enabled ? 'sm:grid-cols-3' : 'sm:grid-cols-2'} gap-3`} data-testid="secondary-metrics">
          <Card className="border border-border shadow-none" data-testid="stat-debt-flow" title={`Debt repayments as % of ${s.debt_basis === 'net' ? 'NET income' : 'gross income'} this month`}>
            <CardContent className="p-3">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Debt Flow</p>
              <p className="text-base font-semibold tabular-nums mt-0.5">{s.debt_flow_pct || 0}%</p>
              <p className="text-[10px] text-muted-foreground">{fmt(s.debt_flow_amount || 0)} of {s.debt_basis === 'net' ? 'net' : 'gross'}</p>
            </CardContent>
          </Card>
          <Card className="border border-border shadow-none">
            <CardContent className="p-3">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Investment Flow</p>
              <p className="text-base font-semibold tabular-nums mt-0.5 text-emerald-600 dark:text-emerald-400">{s.investment_flow_pct || 0}%</p>
              <p className="text-[10px] text-muted-foreground">{fmt(s.investment_flow_amount || 0)}/mo</p>
            </CardContent>
          </Card>
          {s.tax_enabled && (
            <Card className="border border-amber-200 dark:border-amber-900/40 shadow-none" data-testid="stat-tax-ytd">
              <CardContent className="p-3">
                <p className="text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-400">Tax Owed YTD</p>
                <p className="text-base font-semibold tabular-nums mt-0.5 text-amber-700 dark:text-amber-400">{fmt(s.tax_owed_ytd || 0)}</p>
                <p className="text-[10px] text-muted-foreground">Settle by year-end · this month: {fmt(s.tax_owed_month || 0)}</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Refunds banner */}
      {s.refunds > 0 && (
        <div className="p-3 rounded-md bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 flex items-center justify-between" data-testid="refunds-banner">
          <div className="text-sm text-green-700 dark:text-green-400">
            <span className="font-medium">{fmt(s.refunds)}</span> in refunds/credits this month — already deducted from expenses
          </div>
          <Button variant="ghost" size="sm" className="text-xs text-green-700 dark:text-green-400 h-7" onClick={() => navigate(`/transactions?year=${currentYear}&month=${currentMonth}&search=credit`)}>View</Button>
        </div>
      )}

      {/* Budget overage alerts (Item 12) */}
      {Array.isArray(s.budget_alerts) && s.budget_alerts.length > 0 && (
        <div className="p-3 rounded-md bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 space-y-1" data-testid="budget-alerts-banner">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-red-700 dark:text-red-400">{s.budget_alerts.length} category cap{s.budget_alerts.length > 1 ? 's' : ''} exceeded</p>
            <Button variant="ghost" size="sm" className="text-xs text-red-700 dark:text-red-400 h-7" onClick={() => navigate('/settings')}>Adjust caps</Button>
          </div>
          <div className="flex flex-wrap gap-2 text-xs">
            {s.budget_alerts.map((a) => (
              <span key={a.category} data-testid={`budget-alert-${a.category}`} className="px-2 py-0.5 rounded bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
                {a.category}: {a.actual_pct}% (cap {a.cap_pct}%)
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Pie + Health + Merchants */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="border border-border shadow-none" data-testid="category-pie-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Spending Breakdown</CardTitle></CardHeader>
          <CardContent>
            {catData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={catData} cx="50%" cy="50%" innerRadius={50} outerRadius={85} dataKey="value" paddingAngle={2} stroke="none" className="cursor-pointer" onClick={(_, idx) => handlePieClick(catData[idx])}>
                      {catData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v, name) => [fmt(v), name]} contentStyle={{ fontSize: '12px', borderRadius: '6px', border: '1px solid hsl(var(--border))', background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }} itemStyle={{ color: 'hsl(var(--card-foreground))' }} labelStyle={{ color: 'hsl(var(--card-foreground))' }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-1.5 mt-2">
                  {catData.map((d, i) => (
                    <div key={d.name} className="flex items-center justify-between text-xs cursor-pointer hover:bg-accent/50 rounded px-1 py-0.5 transition-colors" onClick={() => handlePieClick(d)}>
                      <div className="flex items-center gap-2 min-w-0"><div className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} /><span className="truncate text-muted-foreground">{d.name}</span></div>
                      <div className="flex items-center gap-2 shrink-0"><span className="tabular-nums">{s.income > 0 ? ((d.value / s.income) * 100).toFixed(0) : 0}%</span><span className="tabular-nums font-medium w-16 text-right">{fmt(d.value)}</span></div>
                    </div>
                  ))}
                </div>
              </>
            ) : <div className="h-[200px] flex items-center justify-center text-sm text-muted-foreground">No spending data</div>}
          </CardContent>
        </Card>

        <Card className="border border-border shadow-none" data-testid="health-score-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-2"><Activity className="h-4 w-4 text-primary" />Financial Health</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-center gap-3 mb-4">
              <div className="text-3xl font-bold tracking-tight font-['Outfit'] tabular-nums">{h.score || 0}</div>
              <div>
                <Badge className={`border-0 text-xs ${h.rating === 'Excellent' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : h.rating === 'Good' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400' : h.rating === 'Fair' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'}`}>{h.rating || 'No Data'}</Badge>
                <p className="text-xs text-muted-foreground mt-0.5">out of 100</p>
              </div>
            </div>
            <div className="space-y-2.5">
              {(h.details || []).map((d, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">{d.category}{d.savings_grade && d.savings_grade !== 'N/A' ? ` (${d.savings_grade})` : ''}</span>
                  <div className="flex items-center gap-2"><span className="tabular-nums font-medium">{d.percentage}%</span><span className="text-muted-foreground tabular-nums text-[10px]">/ {d.benchmark}%</span><div className={`h-2 w-2 rounded-full ${d.status === 'good' || d.status === 'excellent' ? 'bg-green-500' : d.status === 'over' || d.status === 'under' ? 'bg-red-500' : d.status === 'ok' ? 'bg-amber-500' : 'bg-yellow-500'}`} /></div>
                </div>
              ))}
            </div>
            {h.investment_contributions > 0 && (
              <div className="mt-3 pt-3 border-t border-border" data-testid="health-extras">
                <div className="flex items-center justify-between text-[11px] text-muted-foreground" data-testid="invest-contrib-row">
                  <span>↳ Includes SIP investments this month</span>
                  <span className="tabular-nums">{fmt(h.investment_contributions)}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border border-border shadow-none" data-testid="top-merchants-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Top Merchants</CardTitle></CardHeader>
          <CardContent>
            {(s.top_merchants || []).length > 0 ? (
              <div className="space-y-3">
                {(s.top_merchants || []).slice(0, 7).map((m, i) => (
                  <div key={i} className="cursor-pointer hover:bg-accent/50 rounded px-1 py-0.5 transition-colors" onClick={() => handleMerchantClick(m.merchant)}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2 min-w-0"><span className="text-xs text-muted-foreground w-4 tabular-nums">{i + 1}</span><span className="text-sm truncate">{m.merchant}</span></div>
                      <span className="text-sm font-medium tabular-nums shrink-0">{fmt(m.amount)}</span>
                    </div>
                    <div className="h-1 bg-muted rounded-full overflow-hidden ml-6"><div className="h-full bg-primary/40 rounded-full" style={{ width: `${s.income > 0 ? Math.min((m.amount / s.income) * 100, 100) : 0}%` }} /></div>
                  </div>
                ))}
              </div>
            ) : <div className="h-[200px] flex items-center justify-center text-sm text-muted-foreground">No merchant data</div>}
          </CardContent>
        </Card>
      </div>

      {/* 50/30/20 Rule */}
      <BudgetRuleCard data={budgetRule} />

      {/* Daily Expenses */}
      <Card className="border border-border shadow-none" data-testid="daily-expenses-card">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Daily Expenses &mdash; {monthLabel}</CardTitle></CardHeader>
        <CardContent>
          {dailyData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={dailyData} onClick={handleDailyClick} className="cursor-pointer">
                <defs><linearGradient id="dg" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="hsl(136,18%,35%)" stopOpacity={0.15} /><stop offset="95%" stopColor="hsl(136,18%,35%)" stopOpacity={0} /></linearGradient></defs>
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} interval={2} />
                <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}`} />
                <Tooltip formatter={(v) => [fmt(v), 'Spent']} labelFormatter={(l, p) => p?.[0]?.payload?.date || `Day ${l}`} contentStyle={{ fontSize: '12px', borderRadius: '6px', border: '1px solid hsl(var(--border))', background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }} itemStyle={{ color: 'hsl(var(--card-foreground))' }} labelStyle={{ color: 'hsl(var(--card-foreground))' }} />
                <Area type="monotone" dataKey="amount" stroke="hsl(136,18%,35%)" strokeWidth={2} fill="url(#dg)" dot={false} activeDot={{ r: 4, strokeWidth: 0, fill: 'hsl(136,18%,35%)' }} />
              </AreaChart>
            </ResponsiveContainer>
          ) : <div className="h-[220px] flex items-center justify-center text-sm text-muted-foreground">No daily data</div>}
        </CardContent>
      </Card>
      </div>
    </>
  );
}
